import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import { PrimaryAction, SecondaryAction, MaskPageBack, CharCounter } from '@/components/mask/MaskSystem';
import { dateLocale } from '@/lib/dateLocale';
import { composeInvite } from '@/lib/momentInvite';
import '@/components/mask/mask.css';

/* MOMENTS 1.0 — Initiator: ruhiger Verwaltungsraum. Private first: keine Texte sichtbar. */

export default function MomentManageV2() {
  const { user, API_V2 } = useV2Auth();
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [m, setM] = useState(null);
  const [order, setOrder] = useState([]);
  const [copied, setCopied] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [selfOpen, setSelfOpen] = useState(false);
  const [selfText, setSelfText] = useState('');
  const [participants, setParticipants] = useState([]);
  const [pName, setPName] = useState('');
  const [pEmail, setPEmail] = useState('');
  const [mailInfo, setMailInfo] = useState(null);
  const [inviteText, setInviteText] = useState('');
  const [inviteInit, setInviteInit] = useState(false);
  const [invitePreview, setInvitePreview] = useState(false);
  const [inviteSaved, setInviteSaved] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedDeliver, setSchedDeliver] = useState('');
  const [schedDeadline, setSchedDeadline] = useState('');
  const [schedManual, setSchedManual] = useState(false);
  const [schedSaving, setSchedSaving] = useState(false);

  const isoToLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const openSchedule = () => {
    setSchedDeliver(isoToLocal(m.deliverAt));
    setSchedDeadline(isoToLocal(m.deadline));
    setSchedManual(!m.deliverAt);
    setSchedOpen(true);
  };

  const saveSchedule = async () => {
    if (schedSaving) return;
    setSchedSaving(true);
    try {
      await axios.put(`${API_V2}/moments/${id}/schedule`, {
        deliverAt: schedManual || !schedDeliver ? null : new Date(schedDeliver).toISOString(),
        deadline: schedDeadline ? new Date(schedDeadline).toISOString() : null,
      }, { withCredentials: true });
      setSchedOpen(false);
      load();
    } catch (_e) { /* bleibt offen */ }
    finally { setSchedSaving(false); }
  };

  const dt = (iso) => (iso ? new Date(iso).toLocaleString(dateLocale(i18n.language), { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);

  const loadParticipants = useCallback(() => {
    axios.get(`${API_V2}/moments/${id}/participants`, { withCredentials: true })
      .then((res) => setParticipants(res.data)).catch(() => {});
  }, [API_V2, id]);

  const sendSelf = async () => {
    if (!selfText.trim()) return;
    await axios.post(`${API_V2}/moments/${id}/contribute-self`, { message: selfText.trim() }, { withCredentials: true });
    setSelfOpen(false); setSelfText(''); load();
  };

  const addParticipant = async () => {
    if (!pName.trim()) return;
    await axios.post(`${API_V2}/moments/${id}/participants`, { participants: [{ name: pName.trim(), email: pEmail.trim() || null }] }, { withCredentials: true });
    setPName(''); setPEmail(''); loadParticipants();
  };

  const sendMail = async (p) => {
    setMailInfo(null);
    try {
      await axios.post(`${API_V2}/moments/${id}/participants/${p.id}/send-email`, {}, { withCredentials: true });
      setMailInfo(t('v2.moments.mailSent', { name: p.name })); loadParticipants();
    } catch (err) { setMailInfo(err.response?.data?.detail || t('v2.moments.mailFail')); }
  };

  const shareLink = async (p) => {
    const link = `${window.location.origin}/moment/einladung/${m.inviteToken}?p=${p.token}`;
    const txt = composeInvite(t, m.recipientName.split(' ')[0], inviteText, link);
    if (navigator.share) { try { await navigator.share({ text: txt }); } catch (_e) {} }
    else { try { await navigator.clipboard.writeText(txt); setMailInfo(t('v2.moments.inviteCopied', { name: p.name })); } catch (_e) {} }
  };

  useEffect(() => {
    document.body.classList.add('cc-no-overlays');
    return () => document.body.classList.remove('cc-no-overlays');
  }, []);

  const load = useCallback(() => {
    axios.get(`${API_V2}/moments/${id}`, { withCredentials: true })
      .then((res) => {
        setM(res.data);
        const ids = res.data.contributions.map((c) => c.id);
        const saved = (res.data.order || []).filter((x) => ids.includes(x));
        setOrder([...saved, ...ids.filter((x) => !saved.includes(x))]);
      })
      .catch((err) => { if (err.response?.status === 401) navigate('/auth', { replace: true }); });
  }, [API_V2, id, navigate]);

  useEffect(() => { if (user) { load(); loadParticipants(); } }, [user, load, loadParticipants]);

  useEffect(() => {
    if (!m || inviteInit) return;
    setInviteText(m.inviteText || t('v2.moments.inviteDefault', { name: m.recipientName.split(' ')[0] }));
    setInviteInit(true);
  }, [m, inviteInit, t]);

  const saveInviteText = async () => {
    const def = t('v2.moments.inviteDefault', { name: m.recipientName.split(' ')[0] });
    const custom = inviteText.trim() === def.trim() ? null : inviteText.trim();
    try {
      await axios.put(`${API_V2}/moments/${id}/invite-text`, { inviteText: custom }, { withCredentials: true });
      setInviteSaved(true); setTimeout(() => setInviteSaved(false), 2000);
    } catch (_e) { /* still shareable client-side */ }
  };

  const resetInviteText = async () => {
    setInviteText(t('v2.moments.inviteDefault', { name: m.recipientName.split(' ')[0] }));
    await axios.put(`${API_V2}/moments/${id}/invite-text`, { inviteText: null }, { withCredentials: true }).catch(() => {});
  };

  if (!user || !m) return <div className="min-h-[60vh] flex items-center justify-center"><p className="text-sm text-[var(--muted)]">{t('v2.common.loading')}</p></div>;

  const inviteLink = `${window.location.origin}/moment/einladung/${m.inviteToken}`;
  const receiveLink = m.receiveToken ? `${window.location.origin}/moment/erleben/${m.receiveToken}` : null;
  const byId = Object.fromEntries(m.contributions.map((c) => [c.id, c]));
  const released = m.status === 'released';
  const openInvites = participants.filter((p) => !p.contributed).length;
  const recipientFirst = m.recipientName.split(' ')[0];

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 2500); } catch (_e) {}
  };

  const move = async (idx, dir) => {
    const next = [...order];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
    await axios.put(`${API_V2}/moments/${id}/order`, { order: next }, { withCredentials: true }).catch(() => {});
  };

  const toEnd = async (idx) => {
    const next = [...order];
    const [x] = next.splice(idx, 1);
    next.push(x);
    setOrder(next);
    await axios.put(`${API_V2}/moments/${id}/order`, { order: next }, { withCredentials: true }).catch(() => {});
  };

  const release = async () => {
    if (releasing) return;
    setReleasing(true);
    try {
      await axios.put(`${API_V2}/moments/${id}/order`, { order }, { withCredentials: true });
      await axios.post(`${API_V2}/moments/${id}/release`, {}, { withCredentials: true });
      load();
    } finally { setReleasing(false); }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-x-hidden" data-testid="moment-manage-page">
      <div className="mask-bg-word mask-bg-top" aria-hidden="true"><span style={{ transform: 'translateX(-8%)' }}>{t('v2.bgWords.moment')}</span></div>
      <div className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-10 pb-28">
        <MaskPageBack onClick={() => navigate('/momente')} data-testid="moment-manage-back" />

        <header className="mb-14">
          <p className="mask-enter text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-4">{m.occasion}</p>
          <h1 className="mask-enter mask-enter-delay-1 mask-headline text-3xl sm:text-4xl text-[var(--text)] mb-4">{t('v2.moments.manageHeadline', { name: m.recipientName })}</h1>
          <p className="text-sm text-[var(--muted)] space-x-3">
            {m.deliverAt && <span data-testid="moment-deliver-info">{t('v2.moments.deliverAt', { date: dt(m.deliverAt) })}</span>}
            {!m.deliverAt && !released && <span>{t('v2.moments.selfRelease')}</span>}
            {m.deadline && <span data-testid="moment-deadline-info">{t('v2.moments.deadlineInfo', { date: dt(m.deadline) })}</span>}
            {!released && (
              <button type="button" onClick={openSchedule} data-testid="moment-edit-schedule-btn"
                className="text-xs text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors">
                {t('v2.moments.editSchedule')}
              </button>
            )}
          </p>
          {schedOpen && !released && (
            <div className="mt-6 space-y-5" data-testid="moment-schedule-editor">
              {!schedManual && (
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mb-2">{t('v2.moments.handover')}</label>
                  <input type="datetime-local" value={schedDeliver} onChange={(e) => setSchedDeliver(e.target.value)}
                    data-testid="schedule-deliver-input" className="mask-input text-base text-[var(--text)] bg-transparent" />
                </div>
              )}
              <button type="button" onClick={() => setSchedManual(!schedManual)} data-testid="schedule-manual-toggle"
                className={`block text-sm py-1 underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors ${schedManual ? 'text-[var(--text)]' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}>
                {schedManual ? `${t('v2.moments.manualRelease')} ✓` : t('v2.moments.manualRelease')}
              </button>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mb-2">{t('v2.moments.deadlineLabel')}</label>
                <input type="datetime-local" value={schedDeadline} onChange={(e) => setSchedDeadline(e.target.value)}
                  data-testid="schedule-deadline-input" className="mask-input text-base text-[var(--text)] bg-transparent" />
              </div>
              <div className="flex items-center gap-4">
                <PrimaryAction onClick={saveSchedule} disabled={schedSaving || (!schedManual && !schedDeliver)} data-testid="schedule-save-btn">
                  {schedSaving ? t('v2.common.loading') : t('v2.common.save')}
                </PrimaryAction>
                <SecondaryAction onClick={() => setSchedOpen(false)} data-testid="schedule-cancel-btn">{t('v2.common.notNow')}</SecondaryAction>
              </div>
            </div>
          )}
          {released && (
            <p className="text-sm text-[var(--text)]/80 mt-3" data-testid="moment-status-released">
              {t('v2.moments.releasedLabel')}{m.opened ? t('v2.moments.opened') : ''}{m.experienced ? t('v2.moments.experienced') : ''}
            </p>
          )}
        </header>

        {!released && (
          <section className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-4">{t('v2.moments.invite')}</p>
            <div className="mb-8" data-testid="manage-invite-editor">
              <div className="flex items-baseline justify-between mb-1.5">
                <p className="text-xs uppercase tracking-[0.15em] text-[var(--muted)]">{t('v2.moments.inviteLabel')}</p>
                {inviteSaved && <span className="text-xs text-[var(--muted)]" data-testid="manage-invite-saved">{t('v2.moments.inviteSaved')}</span>}
              </div>
              <p className="text-xs text-[var(--muted)] mb-4">{t('v2.moments.inviteSub')}</p>
              <textarea
                value={inviteText}
                onChange={(e) => setInviteText(e.target.value.slice(0, 1200))}
                onBlur={saveInviteText}
                data-testid="manage-invite-text-input"
                className="mask-textarea w-full text-base text-[var(--text)] min-h-[14vh]"
              />
              <div className="flex items-center gap-5 mt-2">
                <button type="button" onClick={resetInviteText} data-testid="manage-invite-reset-btn"
                  className="text-xs text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors">
                  {t('v2.moments.inviteReset')}
                </button>
                <button type="button" onClick={() => setInvitePreview(!invitePreview)} data-testid="manage-invite-preview-toggle"
                  className="text-xs text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors">
                  {t('v2.moments.invitePreview')}
                </button>
              </div>
              {invitePreview && (
                <p className="mask-letter text-sm text-[var(--text)]/75 whitespace-pre-line mt-5 pl-4 border-l border-[var(--border)]/50" data-testid="manage-invite-preview">
                  {composeInvite(t, recipientFirst, inviteText, inviteLink)}
                </p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <PrimaryAction onClick={() => copy(composeInvite(t, recipientFirst, inviteText, inviteLink), 'invite')} data-testid="moment-manage-copy-invite">
                {copied === 'invite' ? t('v2.common.copied') : t('v2.moments.copyInvite')}
              </PrimaryAction>
              <a href={`https://wa.me/?text=${encodeURIComponent(composeInvite(t, recipientFirst, inviteText, inviteLink))}`} target="_blank" rel="noreferrer" className="text-sm text-[var(--muted)] hover:text-[var(--text)] py-2 px-1">{t('v2.moments.shareWa')}</a>
            </div>
          </section>
        )}

        {!released && (
          <section className="mb-12" data-testid="moment-self-section">
            {m.selfContributed ? (
              <p className="text-sm text-[var(--text)]/80" data-testid="moment-self-done">{t('v2.moments.selfDone')}</p>
            ) : selfOpen ? (
              <div>
                <textarea value={selfText} onChange={(e) => setSelfText(e.target.value.slice(0, 10000))}
                  placeholder={t('v2.give.msgPlaceholder')} data-testid="moment-self-input"
                  className="mask-textarea text-lg text-[var(--text)] min-h-[14vh] w-full" />
                <CharCounter value={selfText} max={10000} />
                <div className="mt-4 flex gap-3">
                  <PrimaryAction onClick={sendSelf} disabled={!selfText.trim()} data-testid="moment-self-save">{t('v2.moments.sayNow')}</PrimaryAction>
                  <SecondaryAction onClick={() => setSelfOpen(false)}>{t('v2.common.notNow')}</SecondaryAction>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setSelfOpen(true)} data-testid="moment-self-hint"
                className="text-sm text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors">
                {t('v2.moments.selfHint')}
              </button>
            )}
          </section>
        )}

        {!released && (
          <section className="mb-16" data-testid="moment-participants-section">
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-4">{t('v2.moments.personalInvite')}</p>
            <p className="text-xs text-[var(--muted)] mb-5 max-w-md">{t('v2.moments.personalInviteText')}</p>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <input value={pName} onChange={(e) => setPName(e.target.value.slice(0, 60))} placeholder={t('v2.moments.name')} data-testid="participant-name-input"
                className="mask-input text-base flex-1" />
              <input value={pEmail} onChange={(e) => setPEmail(e.target.value.slice(0, 120))} placeholder={t('v2.moments.emailOptional')} data-testid="participant-email-input"
                className="mask-input text-base flex-1" />
              <SecondaryAction onClick={addParticipant} data-testid="participant-add-btn">{t('v2.moments.add')}</SecondaryAction>
            </div>
            {participants.length > 0 && (
              <div className="divide-y divide-[var(--border)]/30" data-testid="participants-list">
                {participants.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-3.5" data-testid={`participant-${p.id}`}>
                    <div>
                      <p className="text-base text-[var(--text)]/85">{p.name}</p>
                      <p className="text-xs text-[var(--muted)] mt-0.5" data-testid={`participant-status-${p.id}`}>
                        {p.contributed ? t('v2.moments.contributed') : t('v2.moments.pending')}{p.email ? ` · ${p.email}` : ''}
                      </p>
                    </div>
                    {!p.contributed && (
                      <div className="flex items-center gap-3 text-xs">
                        {p.email && (
                          <button type="button" onClick={() => sendMail(p)} data-testid={`participant-mail-${p.id}`}
                            className="text-[var(--muted)] hover:text-[var(--text)] py-2 transition-colors underline underline-offset-4 decoration-[var(--muted)]/40">
                            {p.emailSentAt ? t('v2.moments.remind') : t('v2.moments.sendEmail')}
                          </button>
                        )}
                        <button type="button" onClick={() => shareLink(p)} data-testid={`participant-share-${p.id}`}
                          className="text-[var(--muted)] hover:text-[var(--text)] py-2 transition-colors underline underline-offset-4 decoration-[var(--muted)]/40">
                          {t('v2.moments.shareInvite')}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {mailInfo && <p className="text-xs text-[var(--text)]/70 mt-3" data-testid="participant-mail-info">{mailInfo}</p>}
          </section>
        )}

        <section className="mb-16" data-testid="moment-contributions-section">
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-2">{t('v2.moments.contributions')}</p>
          <p className="mask-headline text-xl text-[var(--text)]/85 mb-8" data-testid="moment-contribution-count">
            {m.contributions.length === 0 ? t('v2.moments.noContribs') : (m.contributions.length === 1 ? t('v2.moments.contribsOne', { count: m.contributions.length }) : t('v2.moments.contribsMany', { count: m.contributions.length }))}
          </p>
          <p className="text-xs text-[var(--muted)] mb-6 max-w-md">{t('v2.moments.privacyNote', { name: recipientFirst })}{!released && t('v2.moments.orderNote')}</p>
          <div className="divide-y divide-[var(--border)]/30">
            {order.map((cid, idx) => {
              const c = byId[cid];
              if (!c) return null;
              return (
                <div key={cid} className="flex items-center justify-between py-4" data-testid={`moment-contribution-${cid}`}>
                  <div>
                    <p className="text-base text-[var(--text)]/85">{c.name}</p>
                    <p className="text-xs text-[var(--muted)] mt-0.5">{t('v2.moments.leftAt', { date: dt(c.createdAt) })}</p>
                  </div>
                  {!released && (
                    <div className="flex items-center gap-1 text-[var(--muted)]">
                      <button type="button" onClick={() => move(idx, -1)} data-testid={`moment-order-up-${cid}`} className="w-9 h-9 rounded-full hover:bg-black/5 hover:text-[var(--text)] transition-colors" aria-label={t('v2.moments.up')}>↑</button>
                      <button type="button" onClick={() => move(idx, 1)} data-testid={`moment-order-down-${cid}`} className="w-9 h-9 rounded-full hover:bg-black/5 hover:text-[var(--text)] transition-colors" aria-label={t('v2.moments.down')}>↓</button>
                      <button type="button" onClick={() => toEnd(idx)} data-testid={`moment-order-last-${cid}`} className="text-xs px-2 py-2 hover:text-[var(--text)] transition-colors">{t('v2.moments.toLast')}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {!released ? (
          <section className="pt-8 border-t border-[var(--border)]/40">
            {!confirmRelease ? (
              <>
                <p className="text-sm text-[var(--muted)] mb-6 max-w-md">
                  {m.deliverAt ? t('v2.moments.releaseAutoText') : t('v2.moments.releaseManualText')}
                </p>
                <PrimaryAction onClick={() => setConfirmRelease(true)} disabled={releasing || m.contributions.length === 0} data-testid="moment-release-btn">
                  {t('v2.moments.releaseBtn')}
                </PrimaryAction>
              </>
            ) : (
              <div data-testid="moment-release-confirm">
                <p className="mask-headline text-2xl text-[var(--text)] mb-3">{t('v2.moments.readyFor', { name: recipientFirst })}</p>
                <p className="text-sm text-[var(--muted)] mb-2">
                  {m.contributions.length === 1 ? t('v2.moments.contribsOne', { count: m.contributions.length }) : t('v2.moments.contribsMany', { count: m.contributions.length })}
                  {openInvites > 0 && ` ${openInvites === 1 ? t('v2.moments.openInvitesOne', { count: openInvites }) : t('v2.moments.openInvitesMany', { count: openInvites })}`}
                </p>
                <p className="text-sm text-[var(--muted)] mb-6">{t('v2.moments.releaseWarning')}</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <PrimaryAction onClick={release} disabled={releasing} data-testid="moment-release-confirm-btn">
                    {releasing ? t('v2.common.loading') : t('v2.moments.releaseBtn')}
                  </PrimaryAction>
                  <SecondaryAction onClick={() => setConfirmRelease(false)} data-testid="moment-release-wait-btn">{t('v2.moments.wait')}</SecondaryAction>
                </div>
              </div>
            )}
          </section>
        ) : (
          <section className="pt-8 border-t border-[var(--border)]/40">
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-4">{t('v2.moments.handedOver')}</p>
            <p className="text-sm text-[var(--muted)] mb-6 max-w-md">{t('v2.moments.handOverText', { name: recipientFirst })}</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <PrimaryAction onClick={() => copy(receiveLink, 'receive')} data-testid="moment-copy-receive">
                {copied === 'receive' ? t('v2.common.copied') : t('v2.moments.copyReceive')}
              </PrimaryAction>
              <a href={`https://wa.me/?text=${encodeURIComponent(`${t('v2.moments.forYouWa')}${receiveLink}`)}`} target="_blank" rel="noreferrer" className="text-sm text-[var(--muted)] hover:text-[var(--text)] py-2 px-1">{t('v2.common.perWhatsApp')}</a>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
